import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';
import { ContactLabelSelector } from '../components/ContactLabelSelector';

interface TinyPedido {
  id: string;
  tinyPedidoId: number;
  numeroPedido: number;
  nomeCliente: string | null;
  cpfCnpj: string | null;
  telefone: string | null;
  email: string | null;
  idNotaFiscal: number | null;
  numeroNotaFiscal: string | null;
  nomeEcommerce: string | null;
  numeroEcommerce: string | null;
  situacao: string | null;
  dataPedido: string | null;
  valorTotal: string | null;
  itens: string | null;
}

interface CategoryOption {
  id: string;
  nome: string;
  cor: string;
  funnelId?: string | null;
  funnel?: { id?: string; nome?: string; cor?: string | null } | null;
}

interface PhoneLookupResult {
  numeroPedido: number;
  nomeCliente: string;
  status: 'saved' | 'skipped' | 'no_phone' | 'error';
  message: string;
  phone?: string;
}

export function ERPContactSearchPage() {
  // Pedidos state
  const [pedidos, setPedidos] = useState<TinyPedido[]>([]);
  const [pedidosLoading, setPedidosLoading] = useState(false);
  const [buscandoTudo, setBuscandoTudo] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Filtro de data
  const [dataAtualizacao, setDataAtualizacao] = useState('');
  
  // Filtro de situação
  const [situacao, setSituacao] = useState('entregue');

  // Confirmation dialog
  const [showConfirmBuscar, setShowConfirmBuscar] = useState(false);

  // Selection state
  const [selectedPedidos, setSelectedPedidos] = useState<Set<string>>(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);

  // Phone lookup state
  const [buscandoTelefones, setBuscandoTelefones] = useState(false);
  const { request: requestErpConfirm, modal: erpConfirmModal } = useCreditConfirm();
  const [phoneLookupStatus, setPhoneLookupStatus] = useState('');
  const [phoneLookupResults, setPhoneLookupResults] = useState<PhoneLookupResult[]>([]);
  const [phoneLookupProgress, setPhoneLookupProgress] = useState({ current: 0, total: 0 });

  // Category selector
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // SKU/Description filter for phone lookup
  const [phoneFilterType, setPhoneFilterType] = useState<'descricao' | 'sku'>('descricao');
  const [phoneFilterValues, setPhoneFilterValues] = useState<string[]>(['']);

  // Check if ERP is connected
  const [erpConnected, setErpConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId =
      localStorage.getItem('superadmin_selected_tenant') ||
      localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Carregar categorias
  const loadCategories = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/categorias/all');
      if (response.ok) {
        const data = await response.json();
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  // Carregar pedidos do banco de dados
  const loadPedidos = useCallback(async (page = 1, search = '', sit = '') => {
    setPedidosLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '50',
      });
      if (search) params.append('search', search);
      if (sit) params.append('situacao', sit);

      const response = await authenticatedFetch(`/api/tiny/pedidos?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setPedidos(data.pedidos || []);
        setTotalPages(data.totalPages || 1);
        setTotalPedidos(data.total || 0);
        setCurrentPage(data.page || 1);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setPedidosLoading(false);
    }
  }, []);

  // Check ERP connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/settings');
        if (response.ok) {
          const data = await response.json();
          if (data.tinyApiTokenConfigured || data.tinyApiToken) {
            setErpConnected(true);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar conexão ERP:', error);
      } finally {
        setCheckingConnection(false);
      }
    };
    checkConnection();
    loadPedidos(1, '', situacao);
    loadCategories();
  }, []);

  // Check backend operation status on mount + poll while running
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const checkOperationStatus = async () => {
      try {
        const response = await authenticatedFetch('/api/tiny/pedidos/status');
        if (response.ok) {
          const data = await response.json();
          if (data.buscandoPedidos) {
            setBuscandoTudo(true);
            setStatusMessage(data.pedidosStatus || 'Buscando pedidos no servidor...');
            setStatusType('searching');
          } else if (buscandoTudo && !data.buscandoPedidos) {
            setBuscandoTudo(false);
            if (data.pedidosStatus) {
              setStatusMessage(data.pedidosStatus);
              setStatusType('complete');
            }
            loadPedidos(currentPage, searchQuery, situacao);
          }

          if (data.buscandoTelefones) {
            setBuscandoTelefones(true);
            setPhoneLookupStatus(data.telefonesStatus || 'Buscando telefones no servidor...');
          } else if (buscandoTelefones && !data.buscandoTelefones) {
            setBuscandoTelefones(false);
            if (data.telefonesStatus) {
              setPhoneLookupStatus(data.telefonesStatus);
            }
            loadPedidos(currentPage, searchQuery, situacao);
          }

          if (data.buscandoPedidos || data.buscandoTelefones) {
            if (!pollInterval) {
              pollInterval = setInterval(checkOperationStatus, 3000);
            }
          } else {
            if (pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status das operações:', error);
      }
    };

    checkOperationStatus();

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [buscandoTudo, buscandoTelefones]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Close category dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selection helpers
  const selectedCount = selectAllPages ? totalPedidos : selectedPedidos.size;

  const toggleSelectPedido = (id: string) => {
    setSelectAllPages(false);
    setSelectedPedidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelectAllPages(false);
    const pageIds = pedidos.map(p => p.id);
    const allSelected = pageIds.every(id => selectedPedidos.has(id));
    if (allSelected) {
      setSelectedPedidos(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedPedidos(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const toggleSelectAllPages = () => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setSelectedPedidos(new Set());
    } else {
      setSelectAllPages(true);
      setSelectedPedidos(new Set());
    }
  };

  const isPageAllSelected = pedidos.length > 0 && pedidos.every(p => selectedPedidos.has(p.id));

  // Filtered categories for searchable dropdown
  const filteredCategories = categories.filter(c => `${c.nome} ${c.funnel?.nome || ''}`.toLowerCase().includes(categorySearch.toLowerCase()));
  const groupedCategories = Array.from(filteredCategories.reduce((map, category) => {
    const id = category.funnelId || category.funnel?.id || 'none';
    if (!map.has(id)) map.set(id, { id, name: category.funnel?.nome || 'Sem funil', color: category.funnel?.cor || '#94a3b8', items: [] as CategoryOption[] });
    map.get(id)!.items.push(category);
    return map;
  }, new Map<string, { id: string; name: string; color: string; items: CategoryOption[] }>()).values());

  const selectedCategoryName = categories.find(c => c.id === selectedCategoryId)?.nome || '';

  // BUSCAR TELEFONES via DirectData (SSE)
  const handleBuscarTelefones = async () => {
    if (buscandoTelefones) return;
    if (selectedCount === 0) {
      toast.error('Selecione pelo menos um pedido');
      return;
    }

    setBuscandoTelefones(true);
    setPhoneLookupStatus('Iniciando busca de telefones...');
    setPhoneLookupResults([]);
    setPhoneLookupProgress({ current: 0, total: 0 });

    try {
      const token = localStorage.getItem('auth_token');
      const selectedTenantId =
        localStorage.getItem('superadmin_selected_tenant') ||
        localStorage.getItem('selected_tenant_id');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;

      const body: any = {};
      if (selectAllPages) {
        body.selectAllPages = true;
      } else {
        body.pedidoIds = Array.from(selectedPedidos);
      }
      if (selectedCategoryId) {
        body.categoryId = selectedCategoryId;
      }
      body.tags = selectedTags;
      const cleanFilterVals = phoneFilterValues.filter(v => v.trim() !== '');
      if (cleanFilterVals.length > 0) {
        body.filterType = phoneFilterType;
        body.filterValues = cleanFilterVals;
      }

      const response = await fetch('/api/tiny/pedidos/buscar-telefones', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao buscar telefones');
        setPhoneLookupStatus(errorData.error || 'Erro');
        setBuscandoTelefones(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        toast.error('Erro: stream não disponível');
        setBuscandoTelefones(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = '';

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.substring(6).trim();
          } else if (line === '' && currentEvent && currentData) {
            try {
              const data = JSON.parse(currentData);
              switch (currentEvent) {
                case 'status':
                  setPhoneLookupStatus(data.message);
                  break;
                case 'progress':
                  setPhoneLookupStatus(data.message);
                  setPhoneLookupProgress({ current: data.current, total: data.total });
                  break;
                case 'pedido_result':
                  setPhoneLookupResults(prev => [...prev, data]);
                  break;
                case 'error':
                  toast.error(data.message);
                  setPhoneLookupStatus(data.message);
                  break;
                case 'complete':
                  toast.success(data.message);
                  setPhoneLookupStatus(data.message);
                  loadPedidos(currentPage, searchQuery, situacao);
                  break;
              }
            } catch (e) {}
            currentEvent = '';
            currentData = '';
          } else if (line !== '') {
            buffer += line + '\n';
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao buscar telefones:', error);
      toast.error('Erro ao buscar telefones: ' + (error.message || 'erro desconhecido'));
      setPhoneLookupStatus('Erro na busca');
    } finally {
      setBuscandoTelefones(false);
    }
  };

  // BUSCAR TUDO - SSE via fetch
  const handleBuscarTudo = async () => {
    if (buscandoTudo) return;

    setBuscandoTudo(true);
    setStatusMessage('Iniciando busca de pedidos...');
    setStatusType('searching');

    try {
      const token = localStorage.getItem('auth_token');
      const selectedTenantId =
        localStorage.getItem('superadmin_selected_tenant') ||
        localStorage.getItem('selected_tenant_id');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;

      const response = await fetch('/api/tiny/pedidos/buscar-tudo', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          dataAtualizacao: dataAtualizacao || undefined,
          situacao: situacao || 'entregue',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao buscar pedidos');
        setStatusMessage(errorData.error || 'Erro ao buscar pedidos');
        setStatusType('error');
        setBuscandoTudo(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        toast.error('Erro: stream não disponível');
        setBuscandoTudo(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = '';

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.substring(6).trim();
          } else if (line === '' && currentEvent && currentData) {
            try {
              const data = JSON.parse(currentData);
              switch (currentEvent) {
                case 'status':
                  setStatusMessage(data.message);
                  setStatusType(data.type || 'info');
                  break;
                case 'pedido_saved':
                  setStatusMessage(`Salvando pedido ${data.numeroPedido} (${data.saved}/${data.total})...`);
                  break;
                case 'error':
                  toast.error(data.message);
                  setStatusMessage(data.message);
                  setStatusType('error');
                  break;
                case 'complete':
                  toast.success(data.message);
                  setStatusMessage(data.message);
                  setStatusType('complete');
                  if (data.pedidos) {
                    setPedidos(data.pedidos);
                    setTotalPedidos(data.totalInDb || data.pedidos.length);
                    setTotalPages(Math.ceil((data.totalInDb || data.pedidos.length) / 50));
                  } else {
                    loadPedidos(1, searchQuery, situacao);
                  }
                  break;
              }
            } catch (e) {}
            currentEvent = '';
            currentData = '';
          } else if (line !== '') {
            buffer += line + '\n';
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao buscar pedidos:', error);
      toast.error('Erro ao buscar pedidos: ' + (error.message || 'erro desconhecido'));
      setStatusMessage('Erro na busca');
      setStatusType('error');
    } finally {
      setBuscandoTudo(false);
    }
  };

  // Limpar status quando filtro muda
  useEffect(() => {
    if (statusMessage && dataAtualizacao) {
      setStatusMessage('');
      setStatusType('');
    }
  }, [dataAtualizacao]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadPedidos(1, searchQuery, situacao);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, situacao]);

  const getSituacaoLabel = (sit: string) => {
    const map: Record<string, string> = {
      'aberto': 'Em aberto',
      'aprovado': 'Aprovado',
      'preparando_envio': 'Preparando envio',
      'faturado': 'Faturado',
      'pronto_envio': 'Pronto para envio',
      'enviado': 'Enviado',
      'entregue': 'Entregue',
      'nao_entregue': 'Não Entregue',
      'cancelado': 'Cancelado',
    };
    return map[sit] || sit;
  };

  if (checkingConnection) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Buscar Contato no ERP" subtitle="Busque pedidos do ERP e extraia contatos" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (!erpConnected) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Buscar Contato no ERP" subtitle="Busque pedidos do ERP e extraia contatos" />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-lg shadow p-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">ERP não conectado</h2>
            <p className="text-gray-600 mb-6">
              Para buscar contatos no ERP, primeiro conecte sua conta do Tiny ERP na página de Conexões &gt; ERPs.
            </p>
            <a
              href="/erps"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              Ir para Conexões ERP
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {erpConfirmModal}
      <Header title="Buscar Contato no ERP" subtitle="Busque pedidos do ERP e extraia contatos para prospecção" />

      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Confirmation Dialog - Buscar Todos */}
          {showConfirmBuscar && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Confirmar busca de pedidos</h3>
                <p className="text-sm text-gray-700 mb-2">
                  {dataAtualizacao
                    ? `Você deseja buscar todos os pedidos "${getSituacaoLabel(situacao)}" do Tiny do dia ${dataAtualizacao}?`
                    : `Você deseja buscar todos os pedidos "${getSituacaoLabel(situacao)}" do Tiny?`}
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                  Essa busca pode demorar horas dependendo da quantidade de pedidos dentro do seu ERP.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowConfirmBuscar(false)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirmBuscar(false);
                      handleBuscarTudo();
                    }}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    Sim, buscar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Card de Pedidos */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Pedidos</h2>
              <p className="text-sm text-gray-500 mb-4">
                {totalPedidos > 0
                  ? `${totalPedidos} pedidos no banco de dados`
                  : 'Clique em "Buscar Pedidos" para importar pedidos do Tiny'}
              </p>

              {/* Filtros de data e situação */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Filtros (opcional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Atualizados a partir de</label>
                    <input
                      type="date"
                      value={dataAtualizacao}
                      onChange={(e) => setDataAtualizacao(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Situação do pedido</label>
                    <select
                      value={situacao}
                      onChange={(e) => setSituacao(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="aberto">Em aberto</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="preparando_envio">Preparando envio</option>
                      <option value="faturado">Faturado (atendido)</option>
                      <option value="pronto_envio">Pronto para envio</option>
                      <option value="enviado">Enviado</option>
                      <option value="entregue">Entregue</option>
                      <option value="nao_entregue">Não Entregue</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Filtra pedidos por data de atualização e/ou situação. Sem filtro, busca todos os pedidos.
                </p>
              </div>

              {/* Botão Buscar Tudo */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {dataAtualizacao ? (
                    <span>Pedidos "{getSituacaoLabel(situacao)}" atualizados no dia {dataAtualizacao}</span>
                  ) : (
                    <span>Todos os pedidos {getSituacaoLabel(situacao).toLowerCase()}</span>
                  )}
                </div>
                <button
                  onClick={() => setShowConfirmBuscar(true)}
                  disabled={buscandoTudo || buscandoTelefones}
                  className={`px-6 py-2.5 rounded-md text-white font-medium whitespace-nowrap flex items-center gap-2 ${
                    buscandoTudo
                      ? 'bg-yellow-500 cursor-not-allowed'
                      : buscandoTelefones
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {buscandoTudo ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Buscando pedidos...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Buscar Pedidos
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Status da busca de pedidos */}
            {statusMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                statusType === 'rate_limit'
                  ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  : statusType === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : statusType === 'complete'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}>
                {statusType === 'rate_limit' && (
                  <svg className="animate-pulse w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {buscandoTudo && statusType !== 'rate_limit' && statusType !== 'error' && statusType !== 'complete' && (
                  <svg className="animate-spin h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {statusType === 'complete' && (
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{statusMessage}</span>
              </div>
            )}

            {/* Status da busca de telefones */}
            {phoneLookupStatus && (
              <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                buscandoTelefones
                  ? 'bg-purple-50 border border-purple-200 text-purple-800'
                  : 'bg-green-50 border border-green-200 text-green-800'
              }`}>
                {buscandoTelefones && (
                  <svg className="animate-spin h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {!buscandoTelefones && (
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                <div className="flex-1">
                  <span>{phoneLookupStatus}</span>
                  {buscandoTelefones && phoneLookupProgress.total > 0 && (
                    <div className="mt-2">
                      <div className="w-full bg-purple-200 rounded-full h-1.5">
                        <div
                          className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${(phoneLookupProgress.current / phoneLookupProgress.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-purple-600 mt-1">
                        {phoneLookupProgress.current}/{phoneLookupProgress.total}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Phone lookup results log */}
            {phoneLookupResults.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                <h4 className="text-xs font-semibold text-gray-600 mb-1 sticky top-0 bg-gray-50 pb-1">Resultados da busca de telefones:</h4>
                {phoneLookupResults.map((r, idx) => (
                  <div key={idx} className={`text-xs py-0.5 flex items-center gap-1 ${
                    r.status === 'saved' ? 'text-green-700' :
                    r.status === 'skipped' ? 'text-blue-600' :
                    r.status === 'no_phone' ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    <span className="font-mono">#{r.numeroPedido}</span>
                    <span className="text-gray-400">-</span>
                    <span>{r.nomeCliente || 'Sem nome'}</span>
                    <span className="text-gray-400">-</span>
                    <span className="font-medium">{r.message}</span>
                    {r.phone && <span className="text-gray-500 font-mono ml-1">({r.phone})</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Filter + Category + Phone lookup */}
            {pedidos.length > 0 && (
              <div className="mb-4 bg-gray-50 rounded-lg p-4 space-y-3">
                {/* SKU/Description filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Filtro do Pedido (opcional)</label>
                  <div className="flex gap-2 mb-2">
                    <select
                      value={phoneFilterType}
                      onChange={(e) => setPhoneFilterType(e.target.value as 'descricao' | 'sku')}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    >
                      <option value="descricao">Descrição contém</option>
                      <option value="sku">SKU contém</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    {phoneFilterValues.map((val, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        {idx > 0 && <span className="text-xs text-gray-400 font-medium">OU</span>}
                        <input
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const updated = [...phoneFilterValues];
                            updated[idx] = e.target.value;
                            setPhoneFilterValues(updated);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder={`Valor ${idx + 1} (deixe vazio para buscar todos)`}
                        />
                        {phoneFilterValues.length > 1 && (
                          <button
                            onClick={() => setPhoneFilterValues(phoneFilterValues.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700 text-sm px-2"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {phoneFilterValues.length < 5 && (
                      <button
                        onClick={() => setPhoneFilterValues([...phoneFilterValues, ''])}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        + Adicionar outro valor (OU)
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Filtra pedidos cujos itens contenham o valor no {phoneFilterType === 'descricao' ? 'nome/descrição' : 'código SKU'}. Sem filtro, busca todos os selecionados.
                  </p>
                </div>

                {/* Category selector + Phone lookup button */}
                <div className="flex flex-wrap items-end gap-3">
                  <div className="relative" ref={categoryDropdownRef}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Categoria para salvar contatos</label>
                    <div
                      className="w-64 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm cursor-pointer flex items-center justify-between"
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    >
                      <span className={selectedCategoryName ? 'text-gray-900' : 'text-gray-400'}>
                        {selectedCategoryName || 'Selecione uma categoria (opcional)'}
                      </span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {showCategoryDropdown && (
                      <div className="absolute z-50 mt-1 w-64 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-xl">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            placeholder="Buscar categoria..."
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <div
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${!selectedCategoryId ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600'}`}
                            onClick={() => {
                              setSelectedCategoryId('');
                              setShowCategoryDropdown(false);
                              setCategorySearch('');
                            }}
                          >
                            Sem categoria
                          </div>
                          {groupedCategories.map((group) => <div key={group.id}>
                            <div className="flex items-center gap-2 border-y border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase text-gray-500"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: group.color }} />{group.name}</div>
                            {group.items.map((cat) => <button type="button" key={cat.id} className={`flex w-full items-center gap-2 px-5 py-2 text-left text-sm hover:bg-indigo-50 ${selectedCategoryId === cat.id ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-700'}`} onClick={() => { setSelectedCategoryId(cat.id); setShowCategoryDropdown(false); setCategorySearch(''); }}><span className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.cor || group.color }} /><span className="truncate">{cat.nome}</span></button>)}
                          </div>)}
                          {filteredCategories.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-400">Nenhuma categoria encontrada</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="min-w-[260px] flex-1"><ContactLabelSelector value={selectedTags} onChange={setSelectedTags} /></div>

                  <button
                    onClick={() => requestErpConfirm({
                      title: 'Recuperar telefones (DirectData)',
                      items: [{ featureKey: 'erp_contact_recovery', qty: selectedCount, label: 'consultas (máx.)' }],
                      note: 'Pedidos que já têm telefone/contato salvo são pulados — você paga apenas pelas consultas DirectData efetivas.',
                      confirmLabel: 'Buscar telefones',
                      onConfirm: handleBuscarTelefones,
                    })}
                    disabled={buscandoTelefones || buscandoTudo || selectedCount === 0}
                    className={`px-5 py-2 rounded-md text-white font-medium whitespace-nowrap flex items-center gap-2 text-sm ${
                      buscandoTelefones
                        ? 'bg-purple-500 cursor-not-allowed'
                        : buscandoTudo || selectedCount === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {buscandoTelefones ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Buscando telefones...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Buscar Número de Telefone ({selectedCount})
                      </>
                    )}
                  </button>
                </div>

                {selectedCount > 0 && (
                  <CreditCostNotice
                    items={[{ featureKey: 'erp_contact_recovery', qty: selectedCount, label: 'consultas (máx.)' }]}
                    note="Pedidos que já têm telefone/contato salvo são pulados — você paga apenas pelas consultas DirectData efetivas."
                  />
                )}
              </div>
            )}

            {/* Selection controls above table */}
            {pedidos.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!selectAllPages && isPageAllSelected}
                    onChange={toggleSelectPage}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  Selecionar página
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selectAllPages}
                    onChange={toggleSelectAllPages}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  Selecionar todas as páginas ({totalPedidos})
                </label>
                {selectedCount > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {selectedCount} pedido{selectedCount !== 1 ? 's' : ''} selecionado{selectedCount !== 1 ? 's' : ''}
                  </span>
                )}
                <div className="flex-1 min-w-64">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Pesquisar por nome, CPF, telefone, NF ou e-commerce..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Tabela de pedidos */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={!selectAllPages && isPageAllSelected && pedidos.length > 0}
                        onChange={toggleSelectPage}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        title="Selecionar página"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº Pedido</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome do Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPF/CNPJ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº NF</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-commerce</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Itens</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pedidosLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                        <svg className="animate-spin h-5 w-5 mx-auto mb-2 text-indigo-500" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Carregando pedidos...
                      </td>
                    </tr>
                  ) : pedidos.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                        {searchQuery
                          ? 'Nenhum pedido encontrado para esta pesquisa.'
                          : 'Nenhum pedido importado ainda. Clique em "Buscar Pedidos" para começar.'}
                      </td>
                    </tr>
                  ) : (
                    pedidos.map((pedido) => (
                      <tr
                        key={pedido.id}
                        className={`hover:bg-gray-50 ${(selectAllPages || selectedPedidos.has(pedido.id)) ? 'bg-indigo-50' : ''}`}
                      >
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectAllPages || selectedPedidos.has(pedido.id)}
                            onChange={() => toggleSelectPedido(pedido.id)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {pedido.numeroPedido}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {pedido.nomeCliente || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                          {pedido.cpfCnpj || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {pedido.telefone ? (
                            <span className="text-green-700 font-medium">{pedido.telefone}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {pedido.numeroNotaFiscal || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {pedido.nomeEcommerce || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {pedido.itens ? (
                            <div className="relative group">
                              <button className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium border border-indigo-200 hover:bg-indigo-100 transition-colors">
                                {JSON.parse(pedido.itens).length} {JSON.parse(pedido.itens).length === 1 ? 'item' : 'itens'}
                              </button>
                              <div className="absolute right-0 z-50 hidden group-hover:block w-80 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-xl p-3 mt-1">
                                <h4 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1">Itens do Pedido #{pedido.numeroPedido}</h4>
                                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                                  {JSON.parse(pedido.itens).map((item: any, idx: number) => (
                                    <li key={idx} className="text-xs text-gray-600 border-b border-gray-100 pb-1">
                                      <div className="flex justify-between gap-2">
                                        <span className="flex-1 truncate" title={item.descricao}>{item.descricao}</span>
                                        <span className="text-gray-400 whitespace-nowrap">{item.quantidade} {item.unidade}</span>
                                      </div>
                                      {item.codigo && (
                                        <span className="text-[10px] text-gray-400">SKU: {item.codigo}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Página {currentPage} de {totalPages} ({totalPedidos} pedidos)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadPedidos(currentPage - 1, searchQuery, situacao)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadPedidos(currentPage + 1, searchQuery, situacao)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
